import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Filter, ChevronDown } from 'lucide-react';
import type { SelectionFilters } from '../types/cad';

interface SelectionFilterDropdownProps {
  filters: SelectionFilters;
  onFiltersChange: (filters: SelectionFilters) => void;
}

export function SelectionFilterDropdown({ filters, onFiltersChange }: SelectionFilterDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = (key: keyof SelectionFilters) => {
    onFiltersChange({
      ...filters,
      [key]: !filters[key],
    });
  };

  const handleSelectAll = () => {
    const allTrue: SelectionFilters = {
      blocks: true,
      lines: true,
      circles: true,
      arcs: true,
      shapes: true,
      splines: true,
      sockets: true,
      connections: true,
      labels: true,
      texts: true,
      dimensions: true,
      subsystems: true,
    };
    onFiltersChange(allTrue);
  };

  const handleDeselectAll = () => {
    const allFalse: SelectionFilters = {
      blocks: false,
      lines: false,
      circles: false,
      arcs: false,
      shapes: false,
      splines: false,
      sockets: false,
      connections: false,
      labels: false,
      texts: false,
      dimensions: false,
      subsystems: false,
    };
    onFiltersChange(allFalse);
  };

  // Count active filters
  const activeCount = Object.values(filters).filter(Boolean).length;
  const totalCount = Object.keys(filters).length;
  const allActive = activeCount === totalCount;

  const filterLabels: Record<keyof SelectionFilters, string> = {
    blocks: 'Bauteile/Blöcke',
    lines: 'Linien',
    circles: 'Kreise',
    arcs: 'Bögen',
    shapes: 'Polygone/Shapes',
    splines: 'Splines',
    sockets: 'Anschlüsse (Sockets)',
    connections: 'Verbindungen',
    labels: 'Beschriftungen',
    texts: 'Texte',
    dimensions: 'Bemaßungen',
    subsystems: 'Subsysteme',
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
          allActive
            ? 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            : 'bg-blue-500 hover:bg-blue-600 text-white'
        }`}
        title="Auswahlfilter"
      >
        <Filter className="w-4 h-4" />
        <span className="hidden sm:inline">Filter</span>
        {!allActive && <span className="text-xs">({activeCount}/{totalCount})</span>}
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 9999,
            }}
            className="bg-white border border-gray-300 rounded-md shadow-lg min-w-[280px]"
          >
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-gray-700">Auswahlfilter</span>
                <span className="text-xs text-gray-500">
                  {activeCount} / {totalCount} aktiv
                </span>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={handleSelectAll}
                  className="flex-1 px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded"
                >
                  Alle
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="flex-1 px-2 py-1 text-xs bg-gray-300 hover:bg-gray-400 text-gray-700 rounded"
                >
                  Keine
                </button>
              </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
              <div className="p-2 space-y-1">
                {(Object.keys(filters) as Array<keyof SelectionFilters>).map((key) => (
                  <label
                    key={key}
                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={filters[key]}
                      onChange={() => handleToggle(key)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-700 select-none flex-1">
                      {filterLabels[key]}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-600 italic">
                Nur ausgewählte Elemente können selektiert werden
              </p>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
